import { useMemo } from 'react';
import useIsomorphicLayoutEffect from '../../../hooks/useIsomorphicLayoutEffect';
import type { SetupTransitionOptions, TransitionController } from '../../core-transition-component';
import {
  createTransitionController,
  registerTransitionController,
  unregisterTransitionController,
} from '../../core-transition-component';

/**
 * @example
 * const myRef = useRef<HTMLDivElement>(null);
 *
 * const controller = useTransitionController(() => ({
 *   ref: myRef,
 *   refs: {
 *     myRef,
 *   },
 *   setupTransitionInTimeline(timeline, { myRef }) { ... },
 *   setupTransitionInTimeline(timeline, { myRef }) { ... },
 * }));
 *
 */

export function useTransitionController<T>(
  setupOptions: () => SetupTransitionOptions<T>,
  dependencies?: ReadonlyArray<unknown>,
): TransitionController {
  const controller = useMemo(
    () => createTransitionController(setupOptions()),
    // This hook accepts a caller-provided dependency list, so it cannot be an array literal here.
    // eslint-disable-next-line react-hooks/exhaustive-deps, react-hooks/use-memo
    dependencies ?? [],
  );

  useIsomorphicLayoutEffect(() => {
    registerTransitionController(controller);

    controller.setupTimeline({
      direction: 'in',
    });

    return () => {
      controller.dispose();
      unregisterTransitionController(controller);
    };
  }, [controller]);

  return controller;
}
